import { useEffect, useState, RefObject } from 'react';
import type { ModelViewerElement } from '@google/model-viewer';
import { animationNamesEqual } from '@/utils/modelViewerAnimation';

/**
 * Syncs available GLB animation track names from model-viewer (for animation points).
 * Polls until ref is ready so it works when the viewer mounts after the component (e.g. Create Product).
 */
export function useModelViewerAnimations(
  modelViewerRef: RefObject<ModelViewerElement | null>,
  modelUrl: string
): string[] {
  const [availableAnimations, setAvailableAnimations] = useState<string[]>([]);

  useEffect(() => {
    if (!modelUrl) {
      setAvailableAnimations([]);
      return;
    }
    let loadListenerAttached = false;
    const updateAnimations = () => {
      const mv = modelViewerRef.current;
      if (!mv) return;
      try {
        const animations = (mv as unknown as { availableAnimations?: string[] }).availableAnimations ?? [];
        const valid = animations.filter((name: string) => name && name.trim() !== '');
        setAvailableAnimations((current) => (animationNamesEqual(current, valid) ? current : valid));
      } catch {
        setAvailableAnimations((current) => (current.length === 0 ? current : []));
      }
    };
    const handleLoad = () => setTimeout(updateAnimations, 100);
    const interval = setInterval(() => {
      const mv = modelViewerRef.current;
      if (mv && !loadListenerAttached) {
        mv.addEventListener('load', handleLoad);
        loadListenerAttached = true;
      }
      updateAnimations();
    }, 500);
    updateAnimations();
    return () => {
      clearInterval(interval);
      const mv = modelViewerRef.current;
      if (mv && loadListenerAttached) mv.removeEventListener('load', handleLoad);
    };
  }, [modelUrl]);

  return availableAnimations;
}
